import { forwardRef, type FocusEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { CloseOutlined } from "@ant-design/icons";
import { InputNumber, Select, Space } from "antd";
import {
  PERIOD_UNITS,
  type PeriodUnit,
  type ProductPeriod,
} from "@/api/products";
import AddIcon from "@/assets/add.svg?react";
import Button from "@/components/Button";
import { PERIOD_UNIT_LABEL_I18N, periodKey, sortPeriods } from "../rules";

export const PeriodTags = forwardRef<
  HTMLDivElement,
  {
    value?: ProductPeriod[];
    onChange?: (next: ProductPeriod[]) => void;
    adding: boolean;
    removable: boolean;
    newLen: number | null;
    newUnit: PeriodUnit;
    onAdding: (next: boolean) => void;
    onLen: (next: number | null) => void;
    onUnit: (next: PeriodUnit) => void;
    onConfirm: () => void;
    renderCard: (p: ProductPeriod) => { title: string; price?: string };
    onBlur?: FocusEventHandler<HTMLDivElement>;
  }
>(function PeriodTags(
  {
    value,
    onChange,
    adding,
    removable,
    newLen,
    newUnit,
    onAdding,
    onLen,
    onUnit,
    onConfirm,
    renderCard,
    onBlur,
  },
  ref,
) {
  const { t } = useTranslation();
  const periods = sortPeriods(value ?? []);
  return (
    <div
      ref={ref}
      className="flex flex-col gap-3"
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        onBlur?.(e);
      }}
    >
      <div className="flex flex-wrap items-stretch gap-3">
        {periods.map((p) => {
          const card = renderCard(p);
          return (
            <div
              key={periodKey(p)}
              className="relative min-w-30 rounded-lg border border-line hover:bg-accent-hover hover:border-accent h-16 px-5 py-3 text-primary flex flex-col justify-center items-center group"
            >
              {removable && (
                <div
                  className="w-5 h-5 items-center justify-center absolute right-1 top-1 text-primary  rounded-full cursor-pointer hidden group-hover:flex"
                  onClick={() =>
                    onChange?.(
                      periods.filter((x) => periodKey(x) !== periodKey(p)),
                    )
                  }
                >
                  <CloseOutlined className="size-5" />
                </div>
              )}
              <div className="text-sm">{card.title}</div>
              {card.price && (
                <div className="text-[15px] font-medium">{card.price}</div>
              )}
            </div>
          );
        })}
        {!adding && (
          <Button
            className="!h-16 !px-5 !py-3 text-sm font-medium !text-primary hover:!bg-accent-hover"
            icon={<AddIcon />}
            onClick={() => onAdding(true)}
          >
            {t("products.addPeriod")}
          </Button>
        )}
      </div>
      {adding && (
        <Space className="py-3 border-t border-b border-line border-dashed">
          <InputNumber
            min={1}
            precision={0}
            value={newLen}
            onChange={onLen}
            onPressEnter={onConfirm}
            className="!h-9.5 !w-40"
          />
          <Select
            className="!h-9.5 !w-40"
            value={newUnit}
            onChange={onUnit}
            options={PERIOD_UNITS.map((unit) => ({
              value: unit,
              label: t(PERIOD_UNIT_LABEL_I18N[unit]),
            }))}
          />
          <Button.Text onClick={onConfirm} disabled={!newLen || newLen < 1}>
            {t("products.confirmPeriod")}
          </Button.Text>
          <Button.Text type="danger" onClick={() => onAdding(false)}>
            {t("common.cancel")}
          </Button.Text>
        </Space>
      )}
    </div>
  );
});
